// Response DTOs (server mappers). Ids are strings, dates 'YYYY-MM-DD', instants ISO, money in cents.
import type * as C from '@immoaktas/shared';
import type { AgentMessageDto, AgentProposalDto, AiConsent, AiSkipReason, AllocationKey, Annex, AreaRoomType, BillingReadiness, CommonRoom, CostCategory, DocumentDiscardBlocker, MemberRole, OutdoorArea, PlacementConflict, Salutation, StoredPlacement, TenancyDeleteBlocker, TenancyMoveBlocker, UnitDeleteBlocker } from '@immoaktas/shared';

export type { AiConsent, Annex, CommonRoom, OutdoorArea };

export type SliceDto<T> = { id: string; value: T; validFrom: string; validTo: string | null; sourceEventId: string; supersededBy?: string };
export type Address = { street?: string; zip?: string; city?: string; country?: string };

/** `current`: accepted the versions in LEGAL_VERSIONS (otherwise the banner asks again). */
export type LegalConsentDto = { terms: string; privacy: string; at: string; current: boolean };
export type UserDto = {
  id: string;
  email: string;
  name: string;
  emailVerified: boolean;
  /** New login with an unconfirmed e-mail: mails to tenants, invites and purchases wait (EMAIL_NOT_VERIFIED). */
  emailVerificationRequired?: boolean;
  superadmin?: boolean;
  /** null = never asked */ aiConsent: AiConsent | null;
  /** null = never accepted (older logins, provider sign-ups before the account) */
  legalConsent?: LegalConsentDto | null;
};
export type Me = { user: UserDto; memberships: { accountId: string; accountName: string; role: MemberRole }[]; tenant: boolean; roles: ('landlord' | 'tenant' | 'admin')[] };
export type Session = { accessToken: string; refreshToken?: string; expiresIn: number; user: UserDto };

export type Account = {
  id: string;
  name: string;
  role: MemberRole;
  version: number;
  letterhead: { name: string; address: string; iban?: string; bic?: string; email?: string; phone?: string };
  defaults: { allocationKeys: Partial<Record<CostCategory, AllocationKey>>; vacancyFictivePersons: number; prepaymentSuggestionBufferPct: number; reminderDays: number[]; prepaymentBasis: 'target' | 'actual'; dueDay: number; retentionYears: number; tenantAddress?: 'sie' | 'du'; depositSettleMonths?: number };
  billing: { plan: 'free' | 'paid'; activeUnits: number; override?: { reason: string; until: string | null } };
  deletionRequestedAt?: string;
};
export type Member = { id: string; userId: string; role: MemberRole; propertyIds?: string[]; user: { email: string; name: string } | null };

export type Property = {
  id: string;
  name: string;
  address: Address;
  type: string;
  buildYear?: number;
  heating: { type: string; co2Applicable: boolean; meteringProvider: string | null; consumptionSharePct?: number };
  floors: { id: string; label: string; order: number }[];
  /** `isBuilding`: the group is a building of its own (house view, docs/15 #40). */
  groups: { id: string; label: string; unitIds: string[]; isBuilding?: boolean }[];
  billingPeriodStart: string;
  expectedCostCategories: CostCategory[];
  /** "Haben wir nicht" (Umlage-Assistent); missing on older properties. */
  absentCostCategories?: CostCategory[];
  allocationKeys: Partial<Record<CostCategory, AllocationKey>>;
  /** WEG: total of all Miteigentumsanteile, remembered by the Hausgeld page (ADR-0016). */
  coOwnershipTotal?: number;
  residential: boolean;
  /** Nebenräume, Gemeinschaftsräume, Außenflächen (ADR-0010); missing on older properties. */
  annexes?: Annex[];
  commonRooms?: CommonRoom[];
  outdoorAreas?: OutdoorArea[];
  /** Personen-Gewichtung laut Mietvertrag (docs/15 #3); absent = every person counts 1. */
  personWeights?: PersonWeights;
  /** Own types of "sonstige Betriebskosten" (docs/15 #36); missing on older properties. */
  customCostTypes?: CustomCostType[];
  version: number;
  unitCount?: number;
  /** GET /properties: billable units existing today (vacancy counts against these). */
  billableUnitIds?: string[];
  /** GET /properties: billable units of this property beyond the plan (docs/BILLING.md §5). */
  lockedUnitCount?: number;
  archivedAt?: string;
};
export type PersonWeights = { children: number; babies: number; childUnderAge?: number; babyUnderAge?: number };
export type CustomCostType = { id: string; name: string };
export type Unit = {
  id: string;
  propertyId: string;
  label: string;
  kind: 'apartment' | 'commercial' | 'parking' | 'storage';
  floorId?: string;
  position?: string;
  /** Treppenhaus / Aufgang: column group in the house plan (docs/15 #40). */
  stairwell?: string;
  /** Left-to-right position inside the floor; missing = after the ordered ones (then by label). */
  sortIndex?: number;
  billable: boolean;
  /** Billable unit beyond the plan: data entry works; finalising, sending and tenant invites wait for the unlock. */
  locked: boolean;
  area: SliceDto<number>[];
  heatingArea: SliceDto<number>[];
  status: SliceDto<string>[];
  coOwnershipShare?: number;
  features: string[];
  existsFrom: string;
  existsTo: string | null;
  version: number;
  current: { asOf: string; area: number | null; heatingArea: number | null; status: string; exists: boolean; /** Flächenrechner rooms behind today's area (evidence), null when none were saved. */ areaRooms?: { name: string; centiM2: number; type: AreaRoomType }[] | null };
};
export type Occupants = { adults: number; children: number; babies: number };
export type Tenancy = {
  id: string;
  unitId: string;
  propertyId: string;
  start: string;
  end: string | null;
  kind: string;
  contractNo?: string;
  noticeGivenOn?: string;
  /** Day of the month the rent is due (account default when missing). */
  dueDay?: number;
  isFlatRate: boolean;
  keyOverrides: Partial<Record<CostCategory, AllocationKey>>;
  excludedCategories: CostCategory[];
  /** Own "sonstige Betriebskosten" types the lease does not allow (docs/15 #36). */  excludedCustomTypes?: string[];  /** Commercial: opted for VAT (§ 9 UStG); statements carry a hint (docs/15 #46). */  vatOpted?: boolean;  deposit?: { amountCents: number; type: string; receivedOn?: string; settledOn?: string; interestCents?: number; payoutCents?: number; payoutDueOn?: string; paidOutOn?: string; retainedCents?: number; retainedReleasedOn?: string; retainedPayoutCents?: number; instalments?: 1 | 3; receipts?: { on: string; amountCents: number }[] };
  emailConsent: { given: boolean };
  tenantPortal: { enabled: boolean; showPayments: boolean };
  parties: SliceDto<string[]>[];
  occupants: SliceDto<Occupants>[];
  rentCold: SliceDto<number>[];
  prepaymentOps: SliceDto<number>[];
  prepaymentHeat: SliceDto<number>[];
  version: number;
  unitLabel?: string;
  /** The tenancy's unit is locked (list and detail): no tenant invite until it is unlocked. */
  unitLocked?: boolean;
  balanceCents?: number;
  persons?: { id: string; name: string }[];
  current: { asOf: string; active: boolean; partyIds: string[]; partyNames?: string[]; occupants: Occupants | null; persons: number; rentColdCents: number | null; prepaymentOpsCents: number | null; prepaymentHeatCents: number | null };
};
/** One stay of a person in the landlord's houses (Wohnhistorie): own party period inside the tenancy. */
export type Residence = { tenancyId: string; propertyId: string; propertyName: string; unitId: string; unitLabel: string; from: string; to: string | null; tenancyStart: string; tenancyEnd: string | null };
export type Person = {
  id: string;
  name: string;
  salutation?: Salutation;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  birthDate?: string;
  previousAddress?: Address;
  notes?: string;
  ibans: string[];
  appUserId?: string;
  deceasedOn?: string;
  version: number;
  postalAddress?: SliceDto<Address>[];
  currentAddress?: Address | null;
  tenancyIds?: string[];
  /** Only on GET /persons/:id. */
  residences?: Residence[];
};
/** Picker entry (GET /persons/directory): only what tells persons apart. */
export type PersonDirectoryEntry = Pick<Person, 'id' | 'name' | 'salutation' | 'firstName' | 'lastName' | 'email' | 'phone' | 'mobile' | 'appUserId' | 'deceasedOn'> & { currentAddress: Address | null; residences: Residence[] };
export type TenancyDeletion = { deletable: boolean; blockers: TenancyDeleteBlocker[] };
/** GET /units/:id/backdate?from=: open periods are recalculated, final ones (`needsCorrection`) stay and offer a correction. */
export type UnitBackdatePreview = { from: string; previousFrom: string; affectedPeriods: { id: string; from: string; to: string; status: string; needsCorrection: boolean; statementId: string | null }[] };
/** Check before moving a tenancy to another unit; `clash` = the dates do not fit (overlap, unit not existing yet). */
export type TenancyMoveCheck = { movable: boolean; blockers: TenancyMoveBlocker[]; clash: { message: string; key?: string; params?: Record<string, unknown>; tenancyId?: string } | null; otherProperty: boolean };
export type UnitDeletion = { deletable: boolean; blockers: UnitDeleteBlocker[] };
export type DocumentDiscardCheck = { discardable: boolean; blockers: DocumentDiscardBlocker[]; discarded: boolean };
export type EventDto = { id: string; type: string; label: string; entity: string; entityId: string; effectiveDate: string; recordedAt: string; actor: { kind: string; name: string }; payload: any; reason?: string; correctsEventId?: string; supersededBy?: string; corrected: boolean };
export type EventPreview = { targets: { collection: string; docId: string; ops: any[] }[]; affectedPeriods: { id: string; from: string; to: string; status: string; needsCorrection: boolean }[]; warnings: string[] };

export type Overview = {
  property: Property;
  asOf: string;
  floors: { id: string; label: string; order: number; units: OverviewUnit[] }[];
  unassigned: OverviewUnit[];
  kpis: { units: number; occupied: number; vacant: number; residents: number; documentsToReview: number; /** locked units of this property */ locked?: number };
  /** Account-wide lock summary (all properties). */
  locks?: UnitLockSummary;
};
/** Account-level lock numbers (docs/BILLING.md §5): allowed null = unlimited. */
export type UnitLockSummary = { allowed: number | null; billable: number; locked: number };
export type OverviewUnit = Unit & { tenancy: (Tenancy['current'] & { id: string; start: string; end: string | null }) | null; nextTenancy: { id: string; start: string } | null };

export type MonthStatus = 'covered' | 'manual' | 'review' | 'missing' | 'expected' | 'na';
export type YearFile = {
  year: number;
  availableYears: number[];
  kpis: { units: number; residents: number; documents: number; missing: number; toReview: number; manual: number; apportionableCents: number };
  occupancy: { unitId: string; label: string; segments: { from: string; to: string; kind: 'tenancy' | 'tenancy_new' | 'shared_flat' | 'vacancy' | 'planned'; tenancyId?: string; names: string[]; persons: number }[]; markers: { date: string; eventId: string; type: string; label: string }[] }[];
  costs: { category: CostCategory; label: string; origin: string; amountCents: number; documentIds: string[]; costItemIds: string[]; months: MonthStatus[]; status: string; note?: string }[];
  todayMarker?: string;
};

/** GET /properties/:id/insights?year=: units at a glance, occupancy timeline, what the statement of the year still needs. */
export type UnitInsight = {
  unitId: string;
  label: string;
  kind: Unit['kind'];
  billable: boolean;
  floorId: string | null;
  position: string | null;
  /** The unit exists today (ended units of the selected year are listed too). */
  exists: boolean;
  area: number | null;
  status: string;
  occupancy: {
    vacant: boolean;
    /** Rented: tenancy start. Vacant: day after the last tenancy ended. Never rented: null. */
    since: string | null;
    neverRented: boolean;
    current: { tenancyId: string; start: string; end: string | null; names: string[]; persons: number } | null;
    next: { tenancyId: string; start: string; names: string[] } | null;
    lastTenancyId: string | null;
  };
  /** Target per month of the running tenancy (slices today); null when vacant. */
  income: { rentColdCents: number; prepaymentsCents: number; totalCents: number } | null;
  /**
   * Average monthly costs of the unit: `statements` = finalised statements (periods listed), `area_estimate` = area share of
   * the apportionable costs of whole years (years listed); null = no data yet.
   */
  costs: { monthlyCents: number; source: 'statements' | 'area_estimate'; periods: { from: string; to: string }[]; years: number[] } | null;
  /** Per meter type of the unit in the selected year; `partial` ends at the last reading, `estimated` = interpolated. */
  consumption: { type: string; unit: string; meters: number; status: 'ok' | 'partial' | 'missing'; value: number | null; estimated: boolean; from: string; to: string | null }[];
};
export type ReadinessItem =
  | { kind: 'no_period'; year: number }
  | { kind: 'cost_missing'; category: CostCategory }
  | { kind: 'cost_review'; category: CostCategory; documentIds: string[] }
  | { kind: 'documents_review'; count: number }
  | { kind: 'reading_missing'; meterId: string; serialNo: string; meterType: string; unitId: string | null; date: string }
  | { kind: 'readings_proposed'; count: number };
export type PropertyInsights = {
  propertyId: string;
  year: number;
  today: string;
  availableYears: number[];
  units: UnitInsight[];
  timeline: { from: string; to: string; rows: { unitId: string; label: string; kind: Unit['kind']; segments: { from: string; to: string; kind: 'tenancy' | 'vacancy' | 'planned'; tenancyId?: string; names: string[] }[] }[] };
  readiness: { period: { id: string; from: string; to: string; status: Period['status']; deadline: string } | null; window: { from: string; to: string }; items: ReadinessItem[]; costs: BillingReadiness };
};

export type DocumentDto = {
  id: string;
  propertyId?: string;
  /** Akte (docs/18 V13): unit and tenancy a lease, protocol or letter is filed with. */
  unitId?: string;
  tenancyId?: string;
  sha256: string;
  mime: string;
  size: number;
  originalName: string;
  kind: string;
  channel: string;
  status: 'uploaded' | 'processing' | 'needs_review' | 'ready' | 'failed' | 'duplicate' | 'confirmed';
  vendor?: string;
  invoiceNo?: string;
  fields?: Record<string, any>;
  extraction?: { model: string; confidence: Record<string, number>; at: string };
  lastError?: string;
  /** Set when the AI was not used and the receipt waits for manual entry. */
  aiSkipped?: AiSkipReason;
  /** What the receipt was uploaded for (Umlage-Assistent): defaults where the AI found nothing. */
  uploadHint?: { category?: CostCategory; serviceFrom?: string; serviceTo?: string };
  duplicateOf?: string;
  /** Zuordnungsprüfung (docs/06): where the content says it belongs, and the conflict with where it is filed now. */
  placement?: StoredPlacement;
  placementConflict?: PlacementConflict | null;
  /** Discarded by the landlord (wrong upload): hidden from lists, the detail shows it as discarded. */
  discardedAt?: string | null;
  discardReason?: string;
  /** File retention (docs/15 #31): "Aufbewahren bis …" and legal hold. */
  retainUntil?: string | null;
  legalHold?: boolean;
  createdAt: string;
  version: number;
  readUrl?: string | null;
  costItems?: CostItem[];
  lowConfidenceFields?: string[];
};
/** § 551 Abs. 2 BGB instalments of a deposit (docs/15 #34). */
export type DepositInstalments = { tenancyId: string; instalments: 1 | 3; plan: { no: number; dueOn: string; amountCents: number }[]; receipts: { on: string; amountCents: number }[]; receivedCents: number; openCents: number; overdue: { no: number; dueOn: string; amountCents: number; missingCents: number }[] };
/** When a file of an ended tenancy is deleted (docs/15 #31): due date, or why it stays (legal hold, deposit open). */
export type FileRetention = { due: string; extended: boolean; reason?: undefined } | { due: null; reason: 'legal_hold' | 'deposit_open'; extended?: undefined };
export type Override = { id: string; level: 'cost_total' | 'key_value' | 'recipient_total'; targetId?: string; computed?: number; value: number; reason: string; by: { name: string }; at: string; revertedAt?: string };
export type CostItem = {
  id: string;
  propertyId: string;
  documentId?: string;
  category: CostCategory;
  label: string;
  amountCents: number;
  serviceFrom: string;
  serviceTo: string;
  scope: { kind: 'property' } | { kind: 'group'; groupId: string } | { kind: 'units'; unitIds: string[] };
  allocationKey?: AllocationKey;
  apportionable: 'yes' | 'partial' | 'no';
  nonApportionableCents?: number;
  laborCents35a?: number;
  origin: 'document' | 'manual' | 'import';
  manual?: { reason: string };
  /** Parts of one invoice split by outdoor areas share this id (ADR-0010). */
  splitGroupId?: string;
  /** Own type of "sonstige Betriebskosten" (property.customCostTypes, docs/15 #36). */
  customTypeId?: string;
  /** WEG Hausgeld (ADR-0016): WEG total of the position and the flat's key values; labor in the WEG total (ADR-0023). */
  externalShare?: { totalCents: number; share: number; totalShares: number; basis: 'mea' | 'statement'; nonApportionableCents?: number; laborCents35a?: number };
  /** Import run (Hausgeld: `hoa:<unitId|property>:<year>`). */
  importBatch?: string;
  overrides: Override[];
  active: boolean;
  inactiveReason?: string;
  version: number;
};
export type Meter = { id: string; propertyId: string; unitId: string | null; type: string; unit: string; serialNo: string; installedOn: string; removedOn: string | null; calibrationDue?: string; label?: string; remoteReadable?: boolean; version?: number; lastReading: { date: string; value: number } | null; calibrationOverdue: boolean;
  /** Readings went into a finalised statement: no delete, no type change (only deactivate). */
  inFinalStatement?: boolean };
export type MeteringResult = { id: string; costCategory: string; unitId: string | null; tenancyId: string | null; amountCents: number; source: string };
export type Reading = { id: string; meterId: string; propertyId?: string; unitId?: string | null; date: string; value: number; source: string; status: 'proposed' | 'confirmed' | 'rejected'; note?: string; estimated?: boolean };
/** GET /readings?details=true (Zählerstand-Verlauf). */
export type ReadingDetail = Reading & {
  propertyId: string;
  unitId: string | null;
  tenancyId?: string;
  photoFileId?: string;
  aiValue?: number;
  version: number;
  createdAt: string;
  reviewedAt?: string;
  createdByKind: 'user' | 'tenant' | 'system' | 'admin' | null;
  createdByName: string;
  reviewedByName: string | null;
  photoUrl: string | null;
  /** Final statement this reading is the basis of: no correction, no deletion. */
  lockedBy: { statementId: string; from: string; to: string } | null;
  /** Installation, removal or swap reading: the date belongs to the meter. */
  meterEvent: boolean;
  corrections: { at: string; reason: string; byName: string; before: { date: string; value: number; note?: string | null; estimated?: boolean } }[];
};

export type RentChangeKind = 'agreement' | 'increase_558' | 'prepayment_560' | 'graduated_557a' | 'index_557b';
export type RentAmounts = { rentColdCents: number | null; prepaymentOpsCents: number | null; prepaymentHeatCents: number | null };
export type RentCheck = { code: string; severity: 'error' | 'warning'; params?: Record<string, string | number> };
export type RentChangeLetterText = { subject: string; to: string; date: string; place: string; paragraphs: string[]; table: { label: string; before: string; after: string; strong?: boolean }[]; closing: string[]; signatures: string[]; consentSlip?: { title: string; text: string; signatures: string[] }; fileName: string };
/** POST /tenancies/:id/rent-changes with preview: amounts, statutory checks, letter text, delivery options. */
export type RentChangePreview = {
  declaredOn: string;
  before: RentAmounts;
  change: Partial<RentAmounts>;
  after: RentAmounts;
  totals: { before: number; after: number; diff: number };
  checks: RentCheck[];
  earliestEffectiveDate: string | null;
  consentDeadline: string | null;
  needsConsent: boolean;
  statement: { id: string; period: { from: string; to: string }; totalCents: number; balanceCents: number; suggestion: { prepaymentCents: number; prepaymentOpsCents: number; prepaymentHeatCents: number } | null } | null;
  letter: RentChangeLetterText;
  delivery: { email: { consent: boolean; addresses: number }; app: { users: number } };
};
export type RentChangeDispatch = { channel: 'download' | 'print' | 'email' | 'app'; at: string; by: { kind: string; id: string; name: string }; ok: boolean; to?: string[]; users?: number; reason?: string };
export type RentChange = {
  id: string;
  tenancyId: string;
  kind: RentChangeKind;
  status: 'pending_consent' | 'applied' | 'declined' | 'withdrawn';
  effectiveDate: string;
  declaredOn: string;
  before: RentAmounts;
  change: Partial<RentAmounts>;
  after: RentAmounts;
  totals: { before: number; after: number; diff: number };
  justification?: string;
  note?: string;
  checks: RentCheck[];
  consentDeadline?: string;
  consent?: { decision: 'given' | 'refused'; date: string; agreedRentColdCents?: number; note?: string; by: { name: string }; at: string };
  withdrawn?: { at: string; by: { name: string }; reason: string };
  eventIds: string[];
  letter: { subject: string; fileName: string; date: string; documentId: string | null };
  dispatches: RentChangeDispatch[];
  createdAt: string;
  createdBy?: { name: string };
};
export type RentDispatchResult = { results: { channel: RentChangeDispatch['channel']; ok: boolean; reason: string | null; recipients: number }[]; rentChange: RentChange };

export type Period = { id: string; propertyId: string; propertyName?: string; from: string; to: string; status: 'collecting' | 'ready' | 'draft' | 'final' | 'sent' | 'closed'; stale: boolean; needsCorrection: boolean; deadline: string; deadlineDaysLeft: number; days: number; isShortPeriod: boolean; /** Entered later via "Frühere Jahre nachtragen" (for the records). */ retroactive?: boolean; co2?: { kgTotal: number; priceCentsPerTon: number; heatingAreaTotal?: number; /** CO2 cost of the flat laut Hausgeldabrechnung (docs/15 #52). */ unitCostCents?: number }; prepaymentBasis?: 'target' | 'actual'; /** CO2KostAufG not applicable, with reason. */ co2NotApplicable?: { reason: string; at: string }; /** § 556 Abs. 3 S. 3 BGB: late statement not the landlord's fault. */ lateNotResponsible?: { reason: string; at: string }; currentStatementId?: string; statements?: { id: string; version: number; status: string; finalizedAt?: string }[] };
/** POST /billing-periods/backfill: created periods (`expired`: 12-month deadline over, records only), skipped years, units taken back. */
export type BackfillResult = { created: (Period & { year: number; expired: boolean })[]; skipped: { year: number; from: string; to: string; existingId: string }[]; backdatedUnits: { unitId: string; label: string; from: string; previousFrom: string }[] };
export type ChecklistItem = { id: string; label: string; status: 'ok' | 'warning' | 'error'; detail: string };
export type Checklist = { period: Period; overall: 'ok' | 'warning' | 'error'; items: ChecklistItem[]; issues: Issue[] };
export type Issue = { code: string; severity: 'error' | 'warning' | 'info'; message: string; costItemId?: string; unitId?: string; tenancyId?: string };
export type StatementLine = { costItemId: string; category: CostCategory; keyValue: string; keyTotal: string; exact: string; cents: number; overridden?: boolean; explainId: string };
export type Recipient = { id: string; kind: 'tenancy' | 'vacancy' | 'owner_use' | 'rounding' | 'excluded' | 'co2_landlord'; label: string; unitId?: string; tenancyId?: string; from: string; to: string; days: number; paidBy: 'TENANT' | 'LANDLORD'; isFlatRate?: boolean; lines: StatementLine[]; prepaymentsCents: number; estimated?: boolean;
  /** The unit is locked right now (beyond the plan, docs/BILLING.md §5): finalising/sending waits for the unlock. */
  locked?: boolean;
  /**
   * The server withheld this recipient's amounts (locked unit in a DRAFT): `lines` is empty and `totalCents`,
   * `balanceCents`, `suggestedMonthlyPrepaymentCents`, `labor35aCents`, `co2` are absent. Final statements are never redacted.
   */
  redacted?: boolean;
  totalCents?: number; balanceCents?: number; suggestedMonthlyPrepaymentCents?: number; labor35aCents?: number;
  /** ADR-0019 (ENGINE 1.2.0): the suggestion split into operating and heating costs; absent in older results. */
  suggestedMonthlyPrepaymentOpsCents?: number; suggestedMonthlyPrepaymentHeatCents?: number;
  co2?: { landlordPct: number; landlordCents: number; tenantCents: number } | null;
  /** § 556 Abs. 3 BGB (ADR-0018): back payment capped after the deadline; `engineBalanceCents` = calculated balance. */
  deadlineCap?: { engineBalanceCents: number; deadline: string };
  /** Commercial tenancy with VAT option: VAT is not shown, add it with a tax advisor (docs/15 #46). */
  vatOpted?: boolean;
};
/** A recipient with its amounts (not redacted): what money views work with. */
export type PaidRecipient = Recipient & { totalCents: number; balanceCents: number; suggestedMonthlyPrepaymentCents: number; labor35aCents: number; co2: { landlordPct: number; landlordCents: number; tenantCents: number } | null };
export const hasAmounts = (r: Recipient): r is PaidRecipient => !r.redacted && typeof r.totalCents === 'number' && typeof r.balanceCents === 'number';
/** Locked units inside a statement (landlord views): finalising/sending waits for the unlock of `needed` units. */
export type StatementLocks = { unitIds: string[]; locked: number; account: UnitLockSummary; needed: number; yearlyPriceCents: number };
export type Statement = {
  id: string;
  billingPeriodId: string;
  propertyId: string;
  version: number;
  previousVersionId: string | null;
  status: 'draft' | 'final' | 'sent';
  engineVersion: string;
  finalizedAt: string | null;
  blocked: boolean;
  issues: Issue[];
  period: { from: string; to: string; days: number };
  costs: { costItemId: string; category: CostCategory; label: string; /** own "sonstige Betriebskosten" type (docs/15 #36) */ customTypeName?: string; periodCents: number; apportionableCents: number; key: AllocationKey; co2LandlordCents?: number }[];
  checksum: { costsCents: number; recipientsCents: number; differenceCents: number };
  recipients: Recipient[];
  delivery: { tenancyId: string | null; delivery: { channel: string; sentAt: string; openedAt?: string } | null }[];
  locks?: StatementLocks;
  /** GET /statements/:id (landlord): unpaid prepayments of the period per tenancy from the tenant ledger (docs/15 #44). */
  prepaymentArrears?: Record<string, number>;
};
export type Explain = { formula: string; inputs: Record<string, string | number>; source: string };

/** `reversesEntryId`: this entry reverses a payment; `reversedById`: this payment was reversed by that entry. */
export type LedgerEntry = { id: string; date: string; type: string; amountCents: number; note?: string; dueDate?: string; /** Statement balance whose statement has not reached the tenancy yet (ADR-0021): not due. */ notDelivered?: boolean; runningBalanceCents: number; bankTxId?: string; reversesEntryId?: string; reversedById?: string | null };
export type PaymentRow = { tenancyId: string; unitLabel: string; targetCents: number; paidCents: number; openCents: number; status: 'paid' | 'partial' | 'open' | 'none'; arrearsWarning: boolean; balanceCents: number };
/** GET /payments/portfolio: money per property (cents), month = running month, ytd = 1 January until today. */
export type PortfolioFigures = { monthTargetCents: number; monthPaidCents: number; ytdTargetCents: number; ytdPaidCents: number; vacancyMonthCents: number; vacancyYtdCents: number; vacantUnits: number; vacantWithoutReference: number; depositsOpenCents: number; depositsOpen: number };
export type Portfolio = { asOf: string; month: string; rows: (PortfolioFigures & { propertyId: string; name: string })[]; totals: PortfolioFigures };
export type BankTx = { id: string; bookingDate: string; amountCents: number; counterpartyName?: string; counterpartyIban?: string; purpose: string; match: { status: 'unmatched' | 'suggested' | 'matched' | 'ignored'; tenancyId?: string; rule?: string; confidence?: number; ledgerEntryId?: string } };
/** `refId`: period (statement_deadline), meter (calibration) or tenancy (notice, deposit); `propertyId` is sent by the server for every kind. */
/** `handoverId`: deadlines from an Übergabeprotokoll (kinds handover_repair, handover_claims; refId = tenancy). */
export type Deadline = { kind: string; date: string; title: string; propertyName: string; propertyId?: string; refId: string; handoverId?: string; severity: 'overdue' | 'soon' | 'later' };
export type NotificationDto = { id: string; kind: string; title: string; body: string; link: string; createdAt: string; readAt?: string };
export type MessageDto = { id: string; tenancyId: string; from: { kind: string; name: string }; text: string; createdAt: string; readAt?: string };
export type ReportDto = { id: string; tenancyId: string; category: 'damage' | 'question' | 'other'; text: string; status: 'open' | 'in_progress' | 'done'; createdAt: string };
/** State of one payment source (docs/BILLING.md). `store`: APP_STORE, PLAY_STORE … (store subscriptions only). */
export type BillingSourceState = { status: string; units: number; active: boolean; periodEnd: string | null; willRenew: boolean; paymentIssue: boolean; store?: string | null; /** Legacy Premium plan (6 units + KI-Assistent). Older servers omit it. */ premium?: boolean; /** Stripe only: the subscription carries the KI-Assistent add-on item. */ ai?: boolean };
/** KI-Assistent add-on (docs/BILLING.md §10): web item of the Stripe subscription or its own store subscription. */
export type AiAddonState = { source: 'stripe' | 'revenuecat'; status: string; active: boolean; periodEnd: string | null; willRenew: boolean; paymentIssue: boolean };
/** One state for every client: override · payment_issue · cancelling · active · expired · free (docs/BILLING.md). */
export type BillingState = 'override' | 'payment_issue' | 'cancelling' | 'active' | 'expired' | 'free';
export type BillingSummary = {
  plan: 'free' | 'paid' | 'unlimited';
  state: BillingState;
  override: unknown;
  /** Source of the effective (highest active) subscription. */
  source: 'stripe' | 'revenuecat' | null;
  activeUnits: number;
  billableUnits: number;
  allowedUnits: number | null;
  canUse: boolean;
  /** More billable units than allowed: the extra ones are locked (docs/BILLING.md §5); nothing is blocked. */
  overLimit: boolean;
  /** Billable units beyond the plan. */
  lockedUnits: number;
  units: UnitLockSummary;
  /** Quantity that unlocks every unit (web: yearly price = yearlyPriceCents). */
  neededUnits: number;
  unusedPaidUnits: number;
  currentPeriodEnd: string | null;
  willRenew: boolean;
  paymentIssue: boolean;
  /** Web payment failed (past_due): finalising and sending wait for it, reading goes on. Older servers omit it. */
  paymentHold?: boolean;
  /** Web and store subscription both active: only the higher one counts. */
  bothActive: boolean;
  stripe: BillingSourceState | null;
  store: BillingSourceState | null;
  yearlyPriceCents: number;
  freeUnits: number;
  /** Legacy Premium active through any source (docs/BILLING.md §10). Older servers omit it. */
  premium?: boolean;
  /** KI-Assistent add-on, null = never bought. Older servers omit it. */
  aiAddon?: AiAddonState | null;
  /** KI-Assistent without the trial: override or premium (= add-on or legacy Premium); null = trial only. Older servers omit it. */
  agent?: 'override' | 'premium' | null;
};
/** Details of a 402 UNITS_LOCKED for the landlord (a tenant gets the code without details). */
export type UnitsLockedDetails = { allowed: number | null; billable: number; locked: number; needed: number; unitIds: string[]; yearlyPriceCents: number };
export const isUnitsLocked = (d: unknown): d is UnitsLockedDetails => !!d && typeof d === 'object' && typeof (d as UnitsLockedDetails).needed === 'number' && typeof (d as UnitsLockedDetails).billable === 'number';
/** Answer of account deletion: what happens to running subscriptions. */
export type DeletionSubscriptions = { web: 'none' | 'ends_at_period_end'; webUntil: string | null; store: { renews: boolean; store: string | null; until: string | null } | null };

// tenant side
/** Where the flat is in the building (floor label, free position); the landlord's internal names are never sent to tenants. */
export type TenantUnitPlaceDto = { floor: string | null; position: string | null };
export type TenantTenancy = { id: string; address: Address; unit: TenantUnitPlaceDto; landlord: string; start: string; end: string | null; active: boolean; showPayments: boolean; /** removed contract party: statements of the own stretch only */ formerParty?: boolean };
export type TenantTenancyDetail = { id: string; unit: TenantUnitPlaceDto & { area: number | null }; property: { address: Address }; start: string; end: string | null; rentColdCents: number | null; prepaymentOpsCents: number | null; prepaymentHeatCents: number | null; occupants: Occupants | null; deposit: { amountCents: number; receivedOn: string | null } | null; documents: { id: string; name: string; kind: string; url: string | null }[]; meters: { id: string; type: string; unit: string; serialNo: string; label: string | null }[]; /** Completed Übergabeprotokolle in force (ADR-0025); older servers omit it. */ handovers?: { id: string; direction: 'move_in' | 'move_out'; date: string; completedAt: string | null }[] };
export type TenantStatementListItem = { id: string; period: { from: string; to: string; days: number }; version: number; totalCents: number; balanceCents: number; /** Correction (P1-5): previous balance and the difference; absent on a first statement. */ previousBalanceCents?: number; differenceCents?: number; sentAt: string | null; /** Older delivered versions of the same period, newest first (docs/15 #51); older servers omit it. */ olderVersions?: { id: string; version: number; totalCents: number; balanceCents: number; sentAt: string | null }[] };export type TenantStatement = { id: string; tenancyId: string; version: number; period: { from: string; to: string; days: number }; yourPeriod: { from: string; to: string; days: number }; lines: { category: CostCategory; label: string; totalCostCents: number; key?: AllocationKey; keyValue: string; keyTotal: string; cents: number; explain: string | null }[]; totalCents: number; prepaymentsCents: number; balanceCents: number; /** Correction (P1-5): balance of the version billed before; null for a first statement. Older servers omit it. */ previousVersion?: { version: number; balanceCents: number } | null; /** What is new to pay (> 0) or comes back (< 0): balance − previous balance. */ differenceCents?: number; /** A newer delivered version of the same period. */ supersededBy?: { id: string; version: number } | null; suggestedMonthlyPrepaymentCents: number; labor35aCents: number; co2: Recipient['co2']; estimated: boolean; /** Commercial tenancy with VAT option (docs/15 #46); older servers omit it. */ vatOpted?: boolean; documents: { id: string; vendor: string; invoiceNo: string; date: string | null; url: string | null }[] };

// admin
/** Environment of a store subscription: production = real money, sandbox = test purchase (App Review, TestFlight), unknown = from before 2026-10-14, not classified yet. */
export type StoreEnvironment = 'production' | 'sandbox' | 'unknown';
export type AdminAccountRow = {
  id: string;
  name: string;
  owner: string;
  plan: string;
  activeUnits: number;
  billableUnits: number;
  internal: boolean;
  override: unknown;
  deletionRequestedAt: string | null;
  createdAt: string;
  /** Counted as revenue (server rule `revenueView`): false for test purchases, unclassified store subscriptions, internal accounts and overrides. Older servers omit it. */
  paying?: boolean;
  /** Environment of an active store subscription, null without one. */
  storeEnvironment?: StoreEnvironment | null;
  /** Legacy Premium (6 units + KI-Assistent) through any active source. Older servers omit it. */
  premium?: boolean;
  /** KI-Assistent add-on active (any source). Older servers omit it. */
  aiAddon?: boolean;
};

// ───────────── KI-Assistent ─────────────
export type AgentThread = {
  conversation: { id: string; title: string; lastMessageAt: string } | null;
  messages: AgentMessageDto[];
  proposals: AgentProposalDto[];
};
/** One chat in the panel's history list (docs/17 §Verlauf). */
export type AgentConversationItem = { id: string; title: string; lastMessageAt: string; turns: number };
export type AgentTurn = { conversationId: string; messages: AgentMessageDto[]; proposals: AgentProposalDto[] };
export type AgentProposalDetail = AgentProposalDto & {
  steps: { label: string; method: string; path: string; entity: string; undoable: boolean; result: { step: number; status: number; ok: boolean; entityId?: string; error?: { code?: string; message?: string } } | null }[];
  undoResults: { step: number; status: number; ok: boolean; error?: { message?: string } }[];
  history: { at: string; byName: string; event: string; detail: unknown }[];
};

/** What needs attention at a unit (GET /units/:id/attention, docs/18). */
export type UnitAttentionKind = 'readings_move_in' | 'readings_move_out' | 'deposit_settle' | 'deposit_payout' | 'deposit_retained' | 'deposit_expected' | 'deposit_instalment_overdue' | 'contract_missing';
export type UnitAttention = { kind: UnitAttentionKind; severity: 'critical' | 'warn' | 'info'; date: string | null; tenancyId: string; names: string; serials?: string[]; amountCents?: number };

// Belege-Eingang and Belegkalender (docs/06, docs/17)
export type SafeReason = 'status' | 'duplicate' | 'unknown_vendor' | 'placement' | 'confidence' | 'no_last_year' | 'amount' | 'lines' | 'advance' | 'weg_statement' | 'final_statement' | 'metering_statement' | 'advance_overlap';
export type ReceiptInbox = { safeConfirm: boolean; safeCount: number; items: (DocumentDto & { safe: boolean; safeReasons: SafeReason[] })[] };
/** GET /dashboard/inbox: facts per property for the Übersicht to-do lines (`arrears` null without money rights). */
export type DashboardInboxRow = {
  propertyId: string;
  period: Period | null;
  periods: Pick<Period, 'from' | 'to' | 'status'>[];
  receiptsMissing: number | null;
  documentsReview: number;
  readingsMissing: number;
  readingsProposed: number;
  arrears: number | null;
};
export type DashboardInbox = { day: string; items: DashboardInboxRow[] };
export type ConfirmSafeResult = { confirmed: { documentId: string; vendor: string | null; amountCents: number }[]; skipped: { documentId: string; reasons: string[] }[] };
export type ExpectedReceipts = {
  year: number;
  received: number;
  missing: { vendor: string; vendorKey: string; category: string | null; arrival: number; arrivalText: string; expectedBy: string; lastYearCents: number; status: 'overdue' | 'upcoming' }[];
  anomalies: { vendor: string; vendorKey: string; category: string | null; amountCents: number; lastYearCents: number; changePct: number }[];
};

// ───────────── Übergabeprotokoll (ADR-0025) ─────────────
export type HandoverSummary = {
  id: string;
  tenancyId: string;
  unitId: string;
  propertyId: string;
  direction: C.HandoverDirection;
  status: C.HandoverStatus;
  date: string;
  completedAt: string | null;
  documentId: string | null;
  replacedBy: string | null;
  replacesId: string | null;
  rooms: number;
  defects: number;
  photos: number;
  signed: boolean;
  dispatched: boolean;
};
export type HandoverFile = { url: string | null; status: string; mime: string; name: string; purpose: 'photo' | 'paper' };
export type HandoverDispatchLog = { channel: C.HandoverChannel; at: string; by: { kind: string; id: string; name: string }; ok: boolean; to?: string[]; reason?: string };
export type Handover = {
  id: string;
  version: number;
  propertyId: string;
  unitId: string;
  tenancyId: string;
  direction: C.HandoverDirection;
  status: C.HandoverStatus;
  date: string;
  time: string | null;
  attendees: C.HandoverAttendee[];
  rooms: C.HandoverRoom[];
  meters: C.HandoverMeter[];
  keys: C.HandoverKey[];
  cleaning: C.HandoverCleaning | null;
  agreements: string;
  notes: string;
  photoIds: string[];
  forwardingAddress: Address | null;
  applyForwardingAddress: boolean;
  signatures: C.HandoverSignature[];
  paperDocumentId: string | null;
  basedOnHandoverId: string | null;
  replacesId: string | null;
  replaceReason?: string;
  replacedBy: string | null;
  completedAt?: string;
  completedBy?: { kind: string; id: string; name: string };
  documentId?: string;
  dispatches: HandoverDispatchLog[];
  addenda: { id: string; at: string; by: { kind: string; id: string; name: string }; text: string }[];
  /** Hash of the current content: a drawn signature with another hash was given for an older version. */
  contentHash: string;
  tenancy: { id: string; start: string; end: string | null; emailConsent: boolean };
  /** Contract parties on the handover day (forwarding address, attendees). */
  parties: { personId: string; name: string; email: string | null; address: Address | null }[];
  meterInfo: { id: string; type: string; unit: string; serialNo: string; label: string | null; active: boolean; lastReading: { date: string; value: number } | null }[];
  /** The earlier protocol (move-in for a move-out) for the comparison, with its rooms and keys. */
  earlier: { id: string; direction: C.HandoverDirection; date: string; rooms: C.HandoverRoom[]; keys: C.HandoverKey[]; meters: C.HandoverMeter[] } | null;
  /** Photos and the paper scan of this and the earlier protocol, by file id (signed links, 10 minutes). */
  files: Record<string, HandoverFile>;
  issues: C.HandoverIssue[];
  completable: boolean;
  summary: HandoverSummary;
  /** After a save that changed the signed content: the signatures were removed. */
  signaturesCleared?: boolean;
};
export type HandoverCompleteResult = Handover & { warnings: C.HandoverIssue[]; bookedReadings: number; addressApplied: boolean };
export type HandoverFileUpload = { fileId: string; duplicate: boolean; uploadUrl: string | null; uploadHeaders?: Record<string, string>; expiresInSeconds?: number; status: string };
export type HandoverDispatchResult = { results: { channel: C.HandoverChannel; ok: boolean; reason: string | null; recipients: number }[] };

/** KI-Assistent: one rated answer with its trace (GET /admin/agent-feedback, docs/17 §Rückmeldung). */
export type AdminAgentFeedback = { id: string; accountId: string; at: string; rating: 'up' | 'down'; comment: string; model: string | null; skills: string[]; question: string | null; answer: string | null; page: string | null; tools: { name: string; ok: boolean; error?: string }[]; costUsd: number | null };
